// src/authz/RequireAnyCan.jsx
import React, { useContext, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import ErrorPage from "../components/ErrorPage";
import { AuthContext } from "../AuthProvider";
import { usePermissionsCtx } from "./PermissionsProvider";
import { canFromGrants } from "./can";
import { usePermissionScopes } from "./PermissionScope";
import { useSupportViewAs } from "../supportViewAs/SupportViewAsProvider";
import { canEnterStaffPortalFromGrants } from "./staffPortalAccess";

export function hasAnyRequiredAccess({ ready, grants, tokenParsed, checks, scopes = null }) {
  if (!Array.isArray(checks) || checks.length === 0) return true;
  return checks.some((check) => canFromGrants({
    ready,
    grants,
    tokenParsed,
    resource: check.resource,
    action: check.action,
    scopes,
  }));
}

export default function RequireAnyCan({ checks, requiredChecks = [], children }) {
  const { authenticated, kcReady, login, keycloak } = useContext(AuthContext);
  const { ready, grants } = usePermissionsCtx();
  const supportViewAs = useSupportViewAs();
  const scopes = usePermissionScopes();
  const loginTriggeredRef = useRef(false);
  const tokenParsed = supportViewAs?.active ? null : keycloak?.tokenParsed;

  const normalizedChecks = useMemo(() => {
    if (!Array.isArray(checks)) return [];
    return checks
      .map((c) => ({ resource: c?.resource, action: c?.action ?? "view" }))
      .filter((c) => !!c.resource);
  }, [checks]);

  const canAny = useMemo(() => {
    return normalizedChecks.some((c) =>
      canFromGrants({ ready, grants, tokenParsed, resource: c.resource, action: c.action, scopes })
    );
  }, [normalizedChecks, ready, grants, tokenParsed, scopes]);

  const normalizedRequiredChecks = useMemo(() => {
    if (!Array.isArray(requiredChecks)) return [];
    return requiredChecks
      .map((c) => ({ resource: c?.resource, action: c?.action ?? "view" }))
      .filter((c) => !!c.resource);
  }, [requiredChecks]);

  const hasRequiredAccess = useMemo(() => hasAnyRequiredAccess({
    ready,
    grants,
    tokenParsed,
    checks: normalizedRequiredChecks,
    scopes,
  }), [normalizedRequiredChecks, ready, grants, tokenParsed, scopes]);

  const canEnterStaffPortal = useMemo(() => {
    return canEnterStaffPortalFromGrants({ ready, grants, tokenParsed });
  }, [ready, grants, tokenParsed]);

  if (!kcReady) return null;

  if (!authenticated) {
    if (!loginTriggeredRef.current) {
      loginTriggeredRef.current = true;
      login();
    }
    return null;
  }

  if (!ready) return null;

  if (canAny && hasRequiredAccess) return children;

  if (!canEnterStaffPortal) return <Navigate to="/portal" replace />;

  return <ErrorPage status={403} message="Geen toegang" />;
}
